import type { ReactElement, ReactNode, RefCallback } from "react";

import { dashboardCommonTexts } from "../locale";
import { NavigationIcon } from "./NavigationIcon";
import { Icon } from "./Icon";
import { Switch } from "./primitives/switch";
import { HeaderActions } from "./HeaderActions";
import type { SaveStatusProps } from "./SaveStatus";

interface ModuleStatusSwitchProps {
  title: string;
  active: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
  onToggle: (active: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
}

function ModuleStatusSwitch({ title, active, activeLabel, inactiveLabel, onToggle, disabled = false, busy = false }: ModuleStatusSwitchProps): ReactElement {
  const texts = dashboardCommonTexts();
  return (
    <div className={`module-status-switch${active ? " is-on" : ""}`}>
      <span>{active ? activeLabel ?? texts.activeModule : inactiveLabel ?? texts.pausedModule}</span>
      <Switch aria-label={title} aria-busy={busy} checked={active} disabled={disabled || busy} onCheckedChange={onToggle} />
    </div>
  );
}

export interface PageHeaderProps {
  className?: string;
  kind?: string;
  title: string;
  titleContent?: ReactNode;
  /** Full text for the heading tooltip when `titleContent` renders something other than `title`. */
  titleTooltip?: string;
  subtitle?: ReactNode;
  meta?: ReactNode;
  icon?: ReactNode | null;
  active?: boolean;
  onToggle?: (active: boolean) => void;
  switchDisabled?: boolean;
  switchBusy?: boolean;
  switchTitle?: string;
  activeLabel?: string;
  inactiveLabel?: string;
  moduleSwitch?: ReactNode;
  badge?: ReactNode;
  badges?: ReactNode;
  saveStatus?: ReactNode;
  save?: SaveStatusProps;
  saveTargetRef?: RefCallback<HTMLDivElement>;
  actions?: ReactNode;
  pageActions?: ReactNode;
  actionHint?: ReactNode;
  overflow?: ReactNode;
  /** Reserve the save cluster's row for pages whose save cluster mounts after the header (settings still loading). */
  reserveSaveRow?: boolean;
  lockHint?: string;
  reserveLockHint?: boolean;
  headingLevel?: "h1" | "h2" | "h3";
}

export function PageHeader({
  className,
  kind = "module",
  title,
  titleContent,
  titleTooltip,
  subtitle,
  meta,
  icon,
  active,
  onToggle,
  switchDisabled = false,
  switchBusy = false,
  switchTitle = title,
  activeLabel,
  inactiveLabel,
  moduleSwitch,
  badge,
  badges,
  saveStatus,
  save,
  saveTargetRef,
  actions,
  pageActions,
  actionHint,
  overflow,
  reserveSaveRow = false,
  lockHint,
  reserveLockHint = false,
  headingLevel = "h1",
}: PageHeaderProps): ReactElement {
  const common = dashboardCommonTexts();
  const switchControl = moduleSwitch ?? (active === undefined || onToggle === undefined
    ? null
    : <ModuleStatusSwitch title={switchTitle} active={active}
      {...(activeLabel === undefined ? {} : { activeLabel })}
      {...(inactiveLabel === undefined ? {} : { inactiveLabel })}
      onToggle={onToggle} disabled={switchDisabled} busy={switchBusy} />);
  const headerActions = actions === undefined ? pageActions : pageActions === undefined ? actions : <>{actions}{pageActions}</>;
  const headerMeta = lockHint === undefined
    ? reserveLockHint
      ? <>{meta}{meta === undefined ? null : " "}<span className="page-header__lock page-header__lock--reserved" aria-hidden="true" /></>
      : meta
    : <>{meta}{meta === undefined ? null : " "}<span className="page-header__lock"><Icon name="lock" size={16} />{lockHint}</span></>;
  const metaContent = reserveLockHint
    ? <div className="page-header__meta">
      {meta === undefined || meta === null || meta === "" ? null : <div className="page-header__meta-content">{meta}</div>}
      <div className="page-header__lock-row">
        {lockHint === undefined
          ? <span className="page-header__lock--reserved" aria-hidden="true" />
          : <span className="page-header__lock"><Icon name="lock" size={16} /><span>{lockHint}</span></span>}
      </div>
    </div>
    : headerMeta === undefined || headerMeta === null || headerMeta === ""
      ? null
      : <div className="page-header__meta">{headerMeta}</div>;
  const Heading = headingLevel;

  return (
    <header data-ui="" className={`page-header module-detail-heading${className === undefined ? "" : ` ${className}`}`}>
      {icon === null ? null : <div className="page-header__icon module-detail__icon module-detail-heading__icon" aria-hidden="true">
        {icon === undefined ? <NavigationIcon kind={kind} className="module-heading-glyph" /> : icon}
      </div>}
      <div className="page-header__main page-header__copy module-detail-heading__copy">
        <div className="page-header__title-row">
          <Heading {...(titleContent === undefined ? { title } : titleTooltip === undefined ? {} : { title: titleTooltip })}>{titleContent ?? title}</Heading>
          {switchControl === null ? null : <div className="page-header__module-switch module-detail__switch">{switchControl}</div>}
          {(badges ?? badge) === undefined || (badges ?? badge) === null ? null : <div className="page-header__badges">{badges ?? badge}</div>}
        </div>
        {subtitle === undefined || subtitle === null || subtitle === "" ? null : <p>{subtitle}</p>}
        {metaContent}
      </div>
      <HeaderActions
        saveStatus={saveStatus}
        {...(save === undefined ? {} : { save })}
        {...(saveTargetRef === undefined ? {} : { saveTargetRef })}
        {...(headerActions === undefined ? {} : { pageActions: headerActions })}
        {...(overflow === undefined ? {} : { overflow })}
        reserveSaveRow={reserveSaveRow}
        moreActionsLabel={common.moreActions}
      />
      {actionHint === undefined || actionHint === null ? null : <div className="page-header__action-hint">{actionHint}</div>}
    </header>
  );
}
